import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Save, RotateCcw, Loader2, Building2, Image as ImageIcon,
  Sparkles, ShoppingBag, ListChecks, Layers, ExternalLink
} from "lucide-react";
import { api, clearToken, getToken, resolveImage } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import JpegUploader from "../components/admin/JpegUploader";
import { Section, Field, Card, AddButton } from "../components/admin/EditorShared";

const FEATURE_ICONS = ["Home", "Egg", "HeartPulse", "ShieldCheck", "Users", "Headphones", "LifeBuoy", "Truck", "Syringe", "Award"];
const PRODUCT_TYPES = ["Yumurtacı", "Etlik & Yumurtacı", "Etlik", "Hindi", "Diğer"];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("company");

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
      return;
    }
    (async () => {
      try {
        await api.get("/auth/verify");
        const res = await api.get("/content");
        setData(res.data);
      } catch {
        clearToken();
        navigate("/admin");
      }
    })();
  }, [navigate]);

  const updateField = (path, value) => {
    setData((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateArrayItem = (key, idx, field, value) => {
    setData((prev) => {
      const next = structuredClone(prev);
      next[key][idx][field] = value;
      return next;
    });
  };

  const removeArrayItem = (key, idx) => {
    setData((prev) => {
      const next = structuredClone(prev);
      next[key].splice(idx, 1);
      return next;
    });
  };

  const addArrayItem = (key, item) => {
    setData((prev) => {
      const next = structuredClone(prev);
      if (!Array.isArray(next[key])) next[key] = [];
      next[key].push(item);
      return next;
    });
  };

  const save = async () => {
    if (!data) return;
    try {
      setSaving(true);
      const { company, heroSlides, features, services, products, principles, navLinks } = data;
      await api.put("/content", { company, heroSlides, features, services, products, principles, navLinks });
      toast({ title: "Kaydedildi", description: "Değişiklikler başarıyla kaydedildi." });
    } catch (err) {
      toast({ title: "Hata", description: err?.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Tüm içeriği varsayılan ayarlara sıfırlamak istediğinizden emin misiniz?")) return;
    try {
      setSaving(true);
      const res = await api.post("/content/reset");
      setData(res.data);
      toast({ title: "Sıfırlandı", description: "İçerik varsayılana döndü." });
    } catch (err) {
      toast({ title: "Hata", description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin");
  };

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-[#b91c1c]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#b91c1c] text-white flex items-center justify-center font-bold">A</div>
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-none">Aktav Yarka</h1>
              <p className="text-xs text-slate-500">Yönetim Paneli</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm" className="gap-2">
              <a href="/" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" /> Siteyi Gör
              </a>
            </Button>
            <Button variant="outline" size="sm" onClick={reset} className="gap-2 text-slate-600">
              <RotateCcw className="w-4 h-4" /> Sıfırla
            </Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#b91c1c] hover:bg-[#991b1b] text-white gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Kaydet
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="gap-2 text-slate-600">
              <LogOut className="w-4 h-4" /> Çıkış
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 h-auto flex flex-wrap gap-1">
            <TabsTrigger value="company" className="gap-2 data-[state=active]:bg-[#b91c1c] data-[state=active]:text-white"><Building2 className="w-4 h-4" /> Firma</TabsTrigger>
            <TabsTrigger value="hero" className="gap-2 data-[state=active]:bg-[#b91c1c] data-[state=active]:text-white"><ImageIcon className="w-4 h-4" /> Hero Slaytlar</TabsTrigger>
            <TabsTrigger value="features" className="gap-2 data-[state=active]:bg-[#b91c1c] data-[state=active]:text-white"><Sparkles className="w-4 h-4" /> Özellikler</TabsTrigger>
            <TabsTrigger value="services" className="gap-2 data-[state=active]:bg-[#b91c1c] data-[state=active]:text-white"><ListChecks className="w-4 h-4" /> Hizmetler</TabsTrigger>
            <TabsTrigger value="products" className="gap-2 data-[state=active]:bg-[#b91c1c] data-[state=active]:text-white"><ShoppingBag className="w-4 h-4" /> Ürünler</TabsTrigger>
            <TabsTrigger value="principles" className="gap-2 data-[state=active]:bg-[#b91c1c] data-[state=active]:text-white"><Layers className="w-4 h-4" /> İlkeler</TabsTrigger>
          </TabsList>

          {/* COMPANY */}
          <TabsContent value="company" className="space-y-4">
            <Section title="Firma Bilgileri" desc="Site genelinde kullanılan iletişim ve istatistik bilgileri.">
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Firma Adı"><Input value={data.company.name} onChange={(e) => updateField("company.name", e.target.value)} /></Field>
                <Field label="Slogan"><Input value={data.company.tagline} onChange={(e) => updateField("company.tagline", e.target.value)} /></Field>
                <Field label="Telefon (Görünen)"><Input value={data.company.phone} onChange={(e) => updateField("company.phone", e.target.value)} /></Field>
                <Field label="WhatsApp Numarası" hint="Uluslararası format, sadece rakam. Örn: 905406881999"><Input value={data.company.whatsapp} onChange={(e) => updateField("company.whatsapp", e.target.value)} /></Field>
                <Field label="E-posta"><Input value={data.company.email} onChange={(e) => updateField("company.email", e.target.value)} /></Field>
                <Field label="Adres"><Input value={data.company.address} onChange={(e) => updateField("company.address", e.target.value)} /></Field>
                <Field label="Yıllık Tecrübe"><Input value={data.company.yearsExp} onChange={(e) => updateField("company.yearsExp", e.target.value)} /></Field>
                <Field label="Mutlu Müşteri"><Input value={data.company.satisfiedCustomers} onChange={(e) => updateField("company.satisfiedCustomers", e.target.value)} /></Field>
                <Field label="Kurulan Kümes"><Input value={data.company.totalCoops} onChange={(e) => updateField("company.totalCoops", e.target.value)} /></Field>
                <Field label="Sağlıklı Yarka"><Input value={data.company.totalChickens} onChange={(e) => updateField("company.totalChickens", e.target.value)} /></Field>
              </div>
              <Field label="Logo">
                <JpegUploader value={data.company.logo} onChange={(v) => updateField("company.logo", v)} aspect="aspect-square max-w-xs" label="" />
              </Field>
            </Section>
          </TabsContent>

          {/* HERO */}
          <TabsContent value="hero" className="space-y-4">
            <Section
              title="Hero Slaytlar"
              desc="Ana sayfa üst kısımda görünen kayan büyük görseller ve metinler."
              actions={<AddButton onClick={() => addArrayItem("heroSlides", { id: Date.now(), title: "Yeni Slayt", subtitle: "", image: "", cta: "Bilgi Al" })} label="Slayt Ekle" />}
            >
              {data.heroSlides.map((s, i) => (
                <Card key={i} title={`Slayt ${i + 1}`} onDelete={() => removeArrayItem("heroSlides", i)}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <Field label="Başlık"><Input value={s.title} onChange={(e) => updateArrayItem("heroSlides", i, "title", e.target.value)} /></Field>
                      <Field label="Alt Başlık"><Textarea rows={3} value={s.subtitle} onChange={(e) => updateArrayItem("heroSlides", i, "subtitle", e.target.value)} /></Field>
                      <Field label="Buton Yazısı"><Input value={s.cta} onChange={(e) => updateArrayItem("heroSlides", i, "cta", e.target.value)} /></Field>
                    </div>
                    <JpegUploader value={s.image} onChange={(v) => updateArrayItem("heroSlides", i, "image", v)} label="Slayt Görseli" />
                  </div>
                </Card>
              ))}
            </Section>
          </TabsContent>

          {/* FEATURES */}
          <TabsContent value="features" className="space-y-4">
            <Section
              title="Özellik Kartları"
              desc="Hero altındaki 3 vurgu kartı."
              actions={<AddButton onClick={() => addArrayItem("features", { id: Date.now(), icon: "Home", title: "Yeni", desc: "" })} label="Ekle" />}
            >
              <div className="grid md:grid-cols-2 gap-4">
                {data.features.map((f, i) => (
                  <Card key={i} title={`Özellik ${i + 1}`} onDelete={() => removeArrayItem("features", i)}>
                    <Field label="İkon">
                      <select value={f.icon} onChange={(e) => updateArrayItem("features", i, "icon", e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-md">
                        {FEATURE_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </Field>
                    <Field label="Başlık"><Input value={f.title} onChange={(e) => updateArrayItem("features", i, "title", e.target.value)} /></Field>
                    <Field label="Açıklama"><Textarea rows={3} value={f.desc} onChange={(e) => updateArrayItem("features", i, "desc", e.target.value)} /></Field>
                  </Card>
                ))}
              </div>
            </Section>
          </TabsContent>

          {/* SERVICES */}
          <TabsContent value="services" className="space-y-4">
            <Section
              title="Hizmetler"
              desc="Hakkımızda bölümündeki maddeler."
              actions={<AddButton onClick={() => addArrayItem("services", "Yeni hizmet")} label="Hizmet Ekle" />}
            >
              <div className="grid sm:grid-cols-2 gap-3">
                {data.services.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={s} onChange={(e) => {
                      const next = structuredClone(data);
                      next.services[i] = e.target.value;
                      setData(next);
                    }} />
                    <Button variant="outline" size="icon" onClick={() => removeArrayItem("services", i)} className="text-red-600 shrink-0">✕</Button>
                  </div>
                ))}
              </div>
            </Section>
          </TabsContent>

          {/* PRODUCTS */}
          <TabsContent value="products" className="space-y-4">
            <Section
              title="Ürünler"
              desc="Kümes hayvanları listesi."
              actions={<AddButton onClick={() => addArrayItem("products", { id: "yeni-" + Date.now(), name: "Yeni Ürün", type: "Yumurtacı", image: "", desc: "", badge: "" })} label="Ürün Ekle" />}
            >
              <div className="grid md:grid-cols-2 gap-4">
                {data.products.map((p, i) => (
                  <Card key={i} title={p.name || `Ürün ${i + 1}`} onDelete={() => removeArrayItem("products", i)}>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Ürün Adı"><Input value={p.name} onChange={(e) => updateArrayItem("products", i, "name", e.target.value)} /></Field>
                      <Field label="Tür">
                        <select value={p.type} onChange={(e) => updateArrayItem("products", i, "type", e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-md">
                          {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </Field>
                    </div>
                    <Field label="Rozet (opsiyonel)" hint="Örn: 'Çok Satan'. Boş bırakılabilir."><Input value={p.badge || ""} onChange={(e) => updateArrayItem("products", i, "badge", e.target.value)} /></Field>
                    <Field label="Açıklama"><Textarea rows={2} value={p.desc} onChange={(e) => updateArrayItem("products", i, "desc", e.target.value)} /></Field>
                    <JpegUploader value={p.image} onChange={(v) => updateArrayItem("products", i, "image", v)} label="Ürün Görseli" aspect="aspect-[4/3]" />
                  </Card>
                ))}
              </div>
            </Section>
          </TabsContent>

          {/* PRINCIPLES */}
          <TabsContent value="principles" className="space-y-4">
            <Section
              title="İlkelerimiz"
              desc="'Neden Aktav Yarka?' bölümündeki 4 kart."
              actions={<AddButton onClick={() => addArrayItem("principles", { icon: "ShieldCheck", title: "Yeni", desc: "" })} label="İlke Ekle" />}
            >
              <div className="grid md:grid-cols-2 gap-4">
                {data.principles.map((p, i) => (
                  <Card key={i} title={`İlke ${i + 1}`} onDelete={() => removeArrayItem("principles", i)}>
                    <Field label="İkon">
                      <select value={p.icon} onChange={(e) => updateArrayItem("principles", i, "icon", e.target.value)} className="w-full h-10 px-3 border border-slate-200 rounded-md">
                        {FEATURE_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </Field>
                    <Field label="Başlık"><Input value={p.title} onChange={(e) => updateArrayItem("principles", i, "title", e.target.value)} /></Field>
                    <Field label="Açıklama"><Textarea rows={3} value={p.desc} onChange={(e) => updateArrayItem("principles", i, "desc", e.target.value)} /></Field>
                  </Card>
                ))}
              </div>
            </Section>
          </TabsContent>
        </Tabs>

        <div className="sticky bottom-4 mt-8 flex justify-end">
          <Button size="lg" onClick={save} disabled={saving} className="bg-[#b91c1c] hover:bg-[#991b1b] text-white shadow-2xl gap-2 h-12 px-6">
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Tüm Değişiklikleri Kaydet
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
